import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '../api/client'

/**
 * De platformbrede meldingsbalk: onderhoud, een storing, een
 * aangekondigde wijziging. Beheerd vanuit het platformpaneel van
 * Pentrical en zichtbaar in élke hub van élke klant.
 *
 * Dit staat los van de meldingen die een klant zélf beheert - het is
 * bewust niets dat vanuit deze omgeving te wijzigen is.
 *
 * Wie wat ziet bepaalt de server: een melding voor alleen ingelogde
 * gebruikers komt niet mee in het antwoord aan een anonieme bezoeker.
 * Deze store filtert daar dus niet nog eens overheen.
 */

export type PlatformAlertVariant = 'information' | 'attention' | 'emergency'

export interface PlatformAlert {
  id: number
  /**
   * Verandert zodra de melding wordt bijgewerkt of opnieuw aangezet.
   * Draagt samen met het id het wegklikken; zie DISMISSED_KEY.
   */
  version: number | null
  variant: PlatformAlertVariant
  message: string
  linkUrl: string | null
  linkLabel: string | null
  dismissible: boolean
}

/**
 * Weggeklikte meldingen worden per browser onthouden, niet per account:
 * de balk staat ook boven documentatie die een anonieme bezoeker leest,
 * en die heeft nergens een profiel om dit in te bewaren.
 */
const DISMISSED_KEY = 'platform_alerts_dismissed'

/** Eén weggeklikte melding, inclusief de versie waarin dat gebeurde. */
function dismissKey(alert: Pick<PlatformAlert, 'id' | 'version'>): string {
  return `${alert.id}:${alert.version ?? 0}`
}

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    // Onleesbare of geblokkeerde opslag mag de balk niet stukmaken;
    // in het slechtste geval blijft een melding staan die je al kende.
    return []
  }
}

function writeDismissed(keys: string[]): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(keys))
  } catch {
    // Privémodus of volle opslag: wegklikken werkt dan alleen deze sessie.
  }
}

export const usePlatformAlertStore = defineStore('platformAlerts', () => {
  const alerts = ref<PlatformAlert[]>([])
  const dismissed = ref<string[]>(readDismissed())
  const isLoaded = ref(false)

  /** Wat er daadwerkelijk in beeld hoort te komen. */
  const visible = computed(() =>
    alerts.value.filter(alert => !dismissed.value.includes(dismissKey(alert)))
  )

  async function fetchAlerts() {
    try {
      const { data } = await api.get('/platform-alerts')
      alerts.value = Array.isArray(data?.data) ? data.data : []
      pruneDismissed()
    } catch {
      // Geen melding kunnen ophalen is geen reden om de documentatie niet
      // te tonen. Stil laten en het bij de volgende paginaweergave opnieuw
      // proberen; dit is chrome, geen inhoud.
      alerts.value = []
    } finally {
      isLoaded.value = true
    }
  }

  function dismiss(alert: PlatformAlert) {
    // Een storingsmelding is niet weg te klikken. Ook hier controleren en
    // niet alleen in de component: dat is de plek waar de regel hoort.
    if (!alert.dismissible) return

    const key = dismissKey(alert)
    if (dismissed.value.includes(key)) return

    dismissed.value = [...dismissed.value, key]
    writeDismissed(dismissed.value)
  }

  /**
   * Weggeklikte meldingen die niet meer bestaan uit de opslag halen.
   *
   * Zonder dit groeit de lijst bij elke melding die je ooit wegklikte en
   * blijft hij in de browser van de bezoeker staan - jaren na de melding.
   */
  function pruneDismissed() {
    const bestaand = new Set(alerts.value.map(dismissKey))
    const overgebleven = dismissed.value.filter(key => bestaand.has(key))

    if (overgebleven.length !== dismissed.value.length) {
      dismissed.value = overgebleven
      writeDismissed(overgebleven)
    }
  }

  return {
    alerts,
    visible,
    isLoaded,
    fetchAlerts,
    dismiss,
  }
})
